import { useState } from 'react';
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles';
import GlobalStyles from '@mui/joy/GlobalStyles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel, { formLabelClasses } from '@mui/joy/FormLabel';
import IconButton, { IconButtonProps } from '@mui/joy/IconButton';
import Link from '@mui/joy/Link';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@providers/AuthProvider';
import { generateCompiledJobs, useGetOpportunity } from '@api/opportunityService';
import MaestroLoader from '@components/MaestroLoader';
import { useTenant } from '@providers/TenantProvider';
import { DialogContent, DialogTitle, Modal, ModalDialog, Table } from '@mui/joy';
import { ObjectContext } from '@providers/ObjectProvider';
import { ObjectRelationship } from '@components/objects/ObjectRelationship';
import { FormProvider, useForm } from 'react-hook-form';
import { ObjectPrimaryInfoEdit } from '@components/objects/ObjectPrimaryInfoEdit';
// import GoogleIcon from './GoogleIcon';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import { useCreateOpportunitySignUp } from '@api/opportunitySignUpService';
import toast from 'react-hot-toast';
import { queryClient } from '@api/queryClient';
import { useDebounce, useLocalStorage } from '@uidotdev/usehooks';

import { events } from '@utils/analytics';

import { maestroLogo } from '@utils/assets';

const signupFields = [
    {
        name: 'name',
        label: 'Job',
        type: 'info',
        content: (object) => <>{object?.job?.name}</>
    },
    {
        name: 'description',
        label: 'Description',
        type: 'info',
        content: (object) => <>{object?.job?.description}</>
    },
    {
        name: 'time_slot',
        label: 'Time Slot',
        type: 'info',
        content: (object) => <>{object?.slot?.name}</>
    },
    {
        name: 'first_name',
        type: 'text',
        placeholder: 'Your first name',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'last_name',
        type: 'text',
        placeholder: 'Your last name',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'email',
        type: 'email',
        placeholder: 'Your email address',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'cell_phone',
        label: 'Phone',
        type: 'tel',
        placeholder: 'Your phone number',
    },
    {
        type: 'submit',
        label: false,
        buttonLabel: 'Sign Up',
    },
];

export default function VolunteerSignup() {
    const [savedSignupInfo, setSavedSignupInfo] = useLocalStorage('signup_info', {});
    const { uuid } = useParams();
    const { tenant } = useTenant();
    const [open, setOpen] = useState(false);

    const formMethods = useForm({
        defaultValues: {
            ...savedSignupInfo,
            // ...tenant,
            // state: tenant.state_
        },
    });

    const {
        setValue,
        reset,
        handleSubmit,
        formState: { isSubmitting, errors }
    } = formMethods;

    const {
        // isPending,
        isLoading,
        isError,
        error,
        data,
        isFetching,
        isPreviousData,
    } = useGetOpportunity(uuid, {
        retry: false,
    });

    const newSignup = useCreateOpportunitySignUp();

    const onChooseSlot = (slot) => {
        reset({
            ...savedSignupInfo,
            job: slot.object,
            slot: slot.slot,
        });
        setOpen(true);
    }

    const onSubmit = (saveData) => {
        const {job, slot, ...restProps} = saveData;

        setSavedSignupInfo({
            first_name: saveData.first_name,
            last_name: saveData.last_name,
            email: saveData.email,
            cell_phone: saveData.cell_phone,
        });

        const submitData = {
            ...restProps,
            opportunity_id: job.opportunity_id,
            opportunity_job_id: job.id,
            opportunity_time_slot_id: job.slot_id,
        };

        const performUpdate = newSignup.mutateAsync(submitData);

        toast.promise(performUpdate, {
            loading: `Saving...`,
            success: `You are all set!`,
            error: `There was an error while saving.`,
        });

        performUpdate.then(() => {
            events.volunteerSignedUp();

            // close
            setOpen(false);

            // reset form
            reset({
                ...savedSignupInfo,
            });

            // invalidate query
            queryClient.invalidateQueries({queryKey: ['opportunities']});
        });
    };

    if(isLoading) {
        return (
            <Stack alignItems={'center'} justifyContent={'center'} height={'100%'} className="unrecoverable-error">
                <Stack gap={4} alignItems={'center'}>
                    <img
                        src={maestroLogo}
                        alt="Maestro Logo"
                        style={{width: '300px', minWidth: '100px'}}
                    />
                    <Typography level="body-sm" textAlign={'center'}>
                        Volunteer sign ups powered by Maestro
                    </Typography>
                    <MaestroLoader centered/>
                </Stack>
            </Stack>
        );
    }

    if(isError || !tenant) {
        return (
            <Stack alignItems={'center'} justifyContent={'center'} height={'100%'} className="unrecoverable-error">
                <Stack gap={4} alignItems={'center'}>
                    <img
                        src={maestroLogo}
                        alt="Maestro Logo"
                        style={{width: '300px', minWidth: '100px'}}
                    />
                    <Typography level="body-sm" textAlign={'center'}>
                        There was an error loading this volunteer sign up form.
                        <br/>
                        Please contact the event organizer.
                    </Typography>
                </Stack>
            </Stack>
        );
    }

    return (<>
        <Box
            // alignItems={'center'}
            // justifyContent={'center'}
            minHeight={'100vh'}
            sx={theme => ({
                backgroundColor: theme.palette.background.level1,
                p: 4,
            })}
        >
            <Box maxWidth={'1200px'} mx={'auto'}>
            <Stack
                sx={theme => ({
                    // mt: 2,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    backgroundColor: '#fff',// theme.palette.background.surface,
                    // p: 4,
                    px: 4,
                    py: 2,
                    boxShadow: 'md',
                    borderRadius: 'lg',
                })}
                maxWidth={'1200px'}
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Box
                    sx={{
                        // width: '100%',
                        // maxWidth: '200px',
                        flex: '0 1 200px',
                        lineHeight: '0',
                    }}
                >
                    <img
                        src={tenant.logo_url ?? maestroLogo}
                        alt={tenant.logo_url ? tenant.name : "Maestro Logo"}
                        style={{
                            // width: '100%', maxWidth: '200px'
                            maxWidth: '200px',
                            maxHeight: '60px',
                            width: 'auto',
                        }}
                    />
                </Box>
                <Box sx={{textAlign: 'right'}}>
                    <Typography
                        sx={{
                            fontSize: 'lg',
                            fontWeight: 'bold'
                        }}
                    >
                        {data.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 'sm',
                            // fontWeight: 'bold'
                        }}
                    >
                        {tenant.name}
                    </Typography>
                </Box>
            </Stack>

            {(!!data.description || data.files.length > 0) && (<>
                <Stack
                    direction={'row'}
                    sx={{
                        px: 4,
                        py: 2,
                        gap: 2,
                        /*
                        ':empty': {
                            display: 'none',
                        }
                        */
                    }}
                >
                    <Box sx={{flex: '1 1 66%'}}>
                        <div dangerouslySetInnerHTML={{__html: data.description}}></div>
                    </Box>
                    {/* todo get first date and last date to show the date range */}
                    {data.files.length > 0 && (<Box sx={{flex: '1 1 34%'}}>
                        <b>Files</b>
                        <br/>
                    {data.files.map((file) => (
                        <a href={file.url} target='_blank'>{file.name}</a>
                    ))}
                    </Box>)}
                </Stack>
            </>)}

            {/*
            <Stack
                direction={'row'}
                sx={{
                    gap: open ? 2 : 0,
                }}
            >
            */}
                {!open && (
                <Box
                    sx={{
                        mt: 2,
                        flex: 1,
                        '--mm-card-height': 'auto',
                    }}
                >
                    <ObjectContext.Provider value={data}>
                        <ObjectRelationship
                            type={'jobs_public'}
                            mutator={generateCompiledJobs}
                            callback={onChooseSlot}
                        />
                    </ObjectContext.Provider>
                </Box>
                )}

                {open && (<>
                <Box sx={{
                    maxWidth: '600px',
                    mx: 'auto',
                    mt: 2,
                }}>
                    <Button
                    size="sm"
                    variant='plain'
                    sx={{
                        mb: 1,
                    }}
                    onClick={()=>setOpen(false)}
                    startDecorator={<NavigateBeforeRoundedIcon/>}
                    >
                        Go Back
                    </Button>
                <form onSubmit={handleSubmit(onSubmit)}>
                <FormProvider {...formMethods}>

                    <ObjectPrimaryInfoEdit
                        title={'Shift Sign Up'}
                        tint={'default'}
                        fields={signupFields}
                        // action={() => (<>afaf</>)}
                        />
                        </FormProvider>
                        </form>
                </Box>
                </>)}

                {/*
                <Box sx={{
                    overflow: 'hidden',
                    transition: 'var(--mm-default-transition)',
                    width: open ? '50%' : 0,
                }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                <FormProvider {...formMethods}>

                    <ObjectPrimaryInfoEdit
                        title={'Sign Up'}
                        tint={'default'}
                        fields={[]}
                        />
                        </FormProvider>
                        </form>
                </Box>
                */}
            {/* </Stack> */}
            </Box>
        </Box>
    </>);

}
